import type { Dispatch, SetStateAction } from 'react';
import { useTranslation } from 'react-i18next';

import { Spinner } from '@/components/ui/spinner';
import { Alert, AlertDescription } from '@/components/ui/alert';

import type { ConsignmentStatusCode, PalletPortion } from '@/entities/consignments';

import { usePalletView } from '../model/use-pallet-view';
import { PalletHeader } from './pallet-header';
import { PalletActionBar } from './pallet-action-bar';
import { PalletLinesTable } from './pallet-lines-table';

interface PalletViewProps {
  consignmentId: string;
  consignmentStatus?: ConsignmentStatusCode;
  isReadOnly?: boolean;
  /** Selected parts of lines, kept by the parent for the create pallet dialog */
  selectedPortions: PalletPortion[];
  onSelectionChange: Dispatch<SetStateAction<PalletPortion[]>>;
}

export function PalletView({
  consignmentId,
  consignmentStatus,
  isReadOnly,
  selectedPortions,
  onSelectionChange
}: PalletViewProps) {
  const { t } = useTranslation();

  const {
    pallets,
    unassignedLines,
    palletGroups,
    isLoading,
    error,
    canManage,
    isSaving,
    selectedLineIdsOf,
    selectLines,
    hasSelectedInPallets,
    expandedRows,
    toggleExpanded,
    moveSelectedToPallet,
    unassignSelected,
    deletePallet
  } = usePalletView({ consignmentId, consignmentStatus, isReadOnly, selectedPortions, onSelectionChange });

  // Loading state
  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Spinner className="h-8 w-8" />
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <Alert variant="destructive">
        <AlertDescription>{t('pallet.error.loadFailed')}</AlertDescription>
      </Alert>
    );
  }

  // Empty state
  if (unassignedLines.length === 0 && palletGroups.length === 0) {
    return (
      <Alert>
        <AlertDescription>{t('pallet.noData')}</AlertDescription>
      </Alert>
    );
  }

  const tableProps = { consignmentId, consignmentStatus, isReadOnly, selectable: canManage };

  return (
    <div className="space-y-3">
      {/* Action bar for selected lines */}
      {canManage && (
        <PalletActionBar
          selectedCount={selectedPortions.length}
          hasSelectedInPallets={hasSelectedInPallets}
          pallets={pallets}
          disabled={isSaving}
          onMoveToPallet={moveSelectedToPallet}
          onUnassign={unassignSelected}
        />
      )}

      {/* Unassigned lines */}
      {unassignedLines.length > 0 && (
        <div>
          <h3 className="text-sm font-medium mb-2">{t('pallet.unassigned')}</h3>
          <PalletLinesTable
            {...tableProps}
            tableId="pallet-unassigned"
            lines={unassignedLines}
            selectedIds={selectedLineIdsOf(null)}
            onSelectionChange={(ids) => selectLines(null, ids)}
          />
        </div>
      )}

      {/* Pallet groups */}
      {palletGroups.map(({ pallet, lines, unitFields }) => {
        const isExpanded = expandedRows.has(pallet.id);

        return (
          <div key={pallet.id}>
            <PalletHeader
              pallet={pallet}
              lineCount={lines.length}
              isExpanded={isExpanded}
              isSaving={isSaving}
              onToggle={() => toggleExpanded(pallet.id)}
              onDelete={canManage ? () => deletePallet(pallet.id) : undefined}
            />

            {isExpanded && (
              <div className="mt-2">
                {lines.length > 0 ? (
                  <PalletLinesTable
                    {...tableProps}
                    tableId={`pallet-${pallet.id}`}
                    lines={lines}
                    selectedIds={selectedLineIdsOf(pallet.id)}
                    onSelectionChange={(ids) => selectLines(pallet.id, ids)}
                    unitFields={unitFields}
                  />
                ) : (
                  <div className="px-4 py-3 text-sm text-muted-foreground italic">{t('pallet.empty')}</div>
                )}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
